import type {
    CustomerDeviceTypeOverview,
    CustomerPayload,
    CustomerResponse,
    MetricOverviewResponse,
} from '@/types/apps/customer.type';
import type { UserPayload } from '@/types/apps/user.type';
import type {
    ApiResponse,
    PaginatedResult,
    QueryParams,
} from '@/types/common/api.type';
import type { BaseApiClientInterface } from '@/types/service/base-api-client.interface';
import type { Creatable } from '@/types/service/creatable.interface';
import type { Deletable } from '@/types/service/deletable.interface';
import type { Importable } from '@/types/service/importable.interface';
import type { Indexable } from '@/types/service/indexable.interface';
import type { Showable } from '@/types/service/showable.interface';
import type { Updatable } from '@/types/service/updatable.interface';

type CustomerListResponse = ApiResponse<PaginatedResult<CustomerResponse>>;

export class CustomerService
    implements Showable, Updatable, Deletable, Creatable, Indexable, Importable
{
    public constructor(private apiClient: BaseApiClientInterface) {}

    public endpoints = {
        customer: {
            base: () => '/api/customers',
            detail: (id: string) => `/api/customers/${id}`,
            overviewCustomerDeviceType: (id: string) =>
                `/api/customers/get-overview-customer-device-type/${id}`,
            technicalMaintenance: () => '/api/customers/technical-maintenance',
            metricOverview: () => '/api/customers/metric-overview',
            import: () => '/api/customers/import',
            createAccount: (id: string) => `/api/customers/${id}/accounts`,
            editAccount: (id: string) => `/api/customers/${id}/account`,
            deleteAccount: () => '/api/customers/accounts',
        },
    };

    public index(params?: QueryParams): Promise<CustomerListResponse> {
        return this.apiClient.fetchApi(this.endpoints.customer.base(), params);
    }

    public detail(
        id: string,
        params?: QueryParams
    ): Promise<ApiResponse<CustomerResponse>> {
        const url = this.endpoints.customer.detail(id);

        return this.apiClient.fetchApi<CustomerResponse>(url, params);
    }

    public create(
        params: CustomerPayload
    ): Promise<ApiResponse<CustomerResponse>> {
        return this.apiClient.postApi(this.endpoints.customer.base(), params);
    }

    public update(
        id: string,
        payload: CustomerPayload
    ): Promise<ApiResponse<CustomerResponse>> {
        const url = this.endpoints.customer.detail(id);

        return this.apiClient.putApi(url, payload);
    }

    public destroy(
        ids: string[],
        isHardDeleted: boolean = false
    ): Promise<ApiResponse<void>> {
        const url = this.endpoints.customer.base();

        return this.apiClient.destroyApi<void>(url, ids, isHardDeleted);
    }

    public getOverviewCustomerDeviceType(
        id: string,
        params?: QueryParams
    ): Promise<ApiResponse<CustomerDeviceTypeOverview>> {
        return this.apiClient.fetchApi(
            this.endpoints.customer.overviewCustomerDeviceType(id),
            params
        );
    }

    public getTechnicalMaintenance(
        params?: QueryParams
    ): Promise<CustomerListResponse> {
        return this.apiClient.fetchApi(
            this.endpoints.customer.technicalMaintenance(),
            params
        );
    }

    public getMetricOverview(
        params?: QueryParams
    ): Promise<ApiResponse<MetricOverviewResponse>> {
        return this.apiClient.fetchApi(
            this.endpoints.customer.metricOverview(),
            params
        );
    }

    public async importFile(file: File): Promise<any> {
        const form = this.apiClient.buildFormData({ file });

        return this.apiClient.postApi<any>(
            this.endpoints.customer.import(),
            form
        );
    }

    public createAccount(
        id: string,
        payload: UserPayload
    ): Promise<ApiResponse<CustomerResponse>> {
        return this.apiClient.postApi(
            this.endpoints.customer.createAccount(id),
            payload
        );
    }

    public updateAccount(
        id: string,
        payload: UserPayload
    ): Promise<ApiResponse<CustomerResponse>> {
        const url = this.endpoints.customer.editAccount(id);

        return this.apiClient.putApi(url, payload);
    }

    public destroyAccount(
        ids: string[],
        isHardDeleted: boolean = false
    ): Promise<ApiResponse<void>> {
        const url = this.endpoints.customer.deleteAccount();

        return this.apiClient.destroyApi<void>(url, ids, isHardDeleted);
    }
}
